import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {Col, Row, Nav, Tab, Card, Dropdown, Collapse, Table} from 'react-bootstrap';
import {BaseModal} from 'components/modals';
import {ModalAlert} from '@/components/alerts';
import Button from '@/components/ui/Button';
import Icon from '@/components/icons/Icon';
import {EVENT_TYPE_CALL} from 'utils/eventConstants';
import axios from 'axios';
import CellLoader from '@/components/loading/CellLoader';
import useData from '@/utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildEventURL, buildURL} from '@/utils/urlBuilder';
import AddNoteForm from './AddNoteForm';
import {useAlert} from '@/components/alerts';
import {Tooltip} from '@/components/tooltips';
import EventLangChangeForm from './EventLangChangeForm';
import CallTranscript from './CallTranscript';
import AudioPlayer from './AudioPlayer';
import AuditTab from './AuditTab';
import EventHeader from './EventHeader';
import i18n from 'i18n';
import {EventActorsList} from './EventActorsList';
import EventEvaluationsTab from './EventEvaluationsTab';
import EventSummary from './EventSummary';
import {getEventEvaluationUrl} from './utils/evaluationsUrl';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import EventAgentCustomerNameBlock from './EventAgentCustomerNameBlock';
import RenderNoDataAvailable, {RenderFieldValue} from 'components/utils/DataNotAvailable';
import {useStoreState} from 'easy-peasy';
import {UserDetails} from '@/entities/user';
import {extractErrorMessage} from '@/utils/errorHandling';
import {
    EventDetails,
    EventIndexDetails,
    EventComment,
    LangFieldChoice,
    Transcript,
    Evaluation,
    ModuleData,
    AppConfig,
    EventDetailBlockWithPlayerProps
} from '../../types/event';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {isModuleEnabled} from '@/utils/modules';

interface UserData extends UserDetails {
    [key: string]: any;
}

const DELETE_CALL_AUDIO_ACTION = 'delete_call_audio';
const REPLACE_ORIGINAL_ACTION = 'replace_original';

const EventDetailBlockWithPlayer = React.memo(
    ({
        activeEventId,
        eventDetails,
    }: EventDetailBlockWithPlayerProps) => {
        const {showSuccess, showError} = useAlert();
        const appConfig: AppConfig = useStoreState((state: any) => state.appConfigs.items);
        const menuStructure = useStoreState((state: any) => state.sidebarMenu.menuStructure);

        const [changeLangModalOpen, setChangeLangModalOpen] = useState(false);
        const [commentModalOpen, setCommentModalOpen] = useState(false);
        const [selectedComment, setSelectedComment] = useState<EventComment | null>(null);
        const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
        const {userData, loading: loadingUserData} = useOptimizedConfig();
        const [extIdCopySuccess, setExtIdCopySuccess] = useState(false);
        const [activeTab, setActiveTab] = useState('call-details');
        const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
        const [confirmAction, setConfirmAction] = useState<{
            message: string;
            url: string;
            functionName: string;
            displayName: string;
        } | null>(null);
        const [isExecuting, setIsExecuting] = useState<boolean>(false);
        const [isEvaluationsTabClicked, setIsEvaluationsTabClicked] = useState(false);
        const [isAuditTabClicked, setIsAuditTabClicked] = useState(false);
        const [isNotesTabClicked, setIsNotesTabClicked] = useState(false);
        const [isTranscriptionTabClicked, setIsTranscriptionTabClicked] = useState(false);
        const [transcript, setTranscript] = useState<Transcript | null>(null);
        const [loadingTranscript, setLoadingTranscript] = useState(true);
        const [data, setData] = useState<{[key: string]: ModuleData} | null>(null);
        const [waveSurfer, setWaveSurfer] = useState<any>(null);
        const [actionModalOpen, setActionModalOpen] = useState(false);
        const [actionModalContent, setActionModalContent] = useState('');
        const [actionModalTitle, setActionModalTitle] = useState('');
        const canAccessAudit = hasPermission('fs.event.audit_trail.read', userData?.permissions, true);

        const getEventEvaluationsPayload = (): Promise<any> =>
            axios.get(buildEventURL('eventEvaluationsPayload', activeEventId));

        const [eventIndexDetails, loadingEventIndexDetails] = useData<EventIndexDetails>(
            buildEventURL('eventIndexDataDetails', activeEventId),
            'eventDetail',
            'setEventIndexDetails',
            'eventIndexDetails'
        );

        const [eventComments, loadingEventComments] = useData<EventComment[]>(
            isNotesTabClicked ? buildEventURL('eventComments', activeEventId) : null,
            'eventDetail',
            'setEventComments',
            'eventComments'
        );

        const [langFieldChoices, loadingLangChoices] = useData<LangFieldChoice[]>(
            buildURL('langFieldChoices', {obj_id: eventDetails?.eventType.id || ''}),
            'langFieldChoices'
        );

        useEffect(() => {
            if (loadingUserData) {return;}
            const fetchData = async (): Promise<void> => {
                try {
                    const response = await getEventEvaluationsPayload();
                    setData(response.data.call);
                } catch (err: unknown) {
                    console.error('Error fetching data:', err instanceof Error ? err.message : 'Unknown error');
                }
            };

            fetchData();
        }, [activeEventId, loadingUserData]);

        useEffect(() => {
            const getTranscript = async (): Promise<void> => {
                let response;
                setLoadingTranscript(true);
                try {
                    response = await axios.get(buildEventURL('getCallDetailViewData', activeEventId), {
                        headers: {'Content-Type': 'application/json'},
                        data: {},
                    });
                    setTranscript(response.data);
                } catch (e: unknown) {
                    if (e && typeof e === 'object' && 'response' in e && e.response && typeof e.response === 'object' && 'status' in e.response && e.response.status === 404) {
                        return;
                    } else {
                        throw e;
                    }
                } finally {
                    setLoadingTranscript(false);
                }
            };
            getTranscript();
        }, [activeEventId]);

        const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
        const [loadingEvaluations, setLoadingEvaluations] = useState(false);

        const fetchEvaluations = useCallback(async (): Promise<void> => {
            setLoadingEvaluations(true);
            try {
                const response = await axios.get(getEventEvaluationUrl(activeEventId));
                if (response.data) {
                    const filteredData = response.data.filter((item: Evaluation) => item.type !== 'summary_text_sample');
                    setEvaluations(filteredData.sort((a: Evaluation, b: Evaluation) => b.priority - a.priority));
                } else {
                    console.error('API response data is undefined');
                }
            } catch (error: unknown) {
                console.error('Error fetching evaluations:', error);
            } finally {
                setLoadingEvaluations(false);
            }
        }, [activeEventId]);

        useEffect(() => {
            fetchEvaluations();
        }, [fetchEvaluations]);

        if (!data) {
            return <CellLoader fullHeight={true} />;
        }

        const handleWaveSurfer = (waveSurferInstance: any): void => {
            setWaveSurfer(waveSurferInstance);
        };

        const closeCommentModal = (): void => {
            setCommentModalOpen(false);
        };

        const closeSettingsModal = (): void => {
            setChangeLangModalOpen(false);
            showSuccess(i18n.t('jt_language_updated_successfully.text'));
        };

        const handleTabSelect = (key: string | null): void => {
            if (key) {
                setActiveTab(key);
                if (key === 'evaluations') {
                    setIsEvaluationsTabClicked(true);
                } else if (key === 'audit') {
                    setIsAuditTabClicked(true);
                } else if (key === 'notes') {
                    setIsNotesTabClicked(true);
                } else if (key === 'transcript') {
                    setIsTranscriptionTabClicked(true);
                }
            }
        };

        const copyExternalId = (extId: string): void => {
            navigator.clipboard.writeText(extId).then(() => {
                setExtIdCopySuccess(true);
                setTimeout(() => setExtIdCopySuccess(false), 2000);
            }).catch((err: unknown) => {
            });
        };

        // Event Actors
        const detailRestrictedFields: string[] = [
            ...(userData?.restrictedFieldKeys || []),
            ...(eventDetails?._restricted_fields || []),
            ...(eventIndexDetails?._restricted_fields || []),
        ];
        const {eventAgent, eventCustomer} = EventActorsList(eventDetails, eventIndexDetails, userData, detailRestrictedFields);

        // Audio Player
        let audioPlayer: React.ReactElement | undefined;
        if (eventDetails?.eventTypeName === EVENT_TYPE_CALL) {
            if (transcript) {
                audioPlayer = (
                    <AudioPlayer
                        eventId={activeEventId}
                        timelineBlock={transcript.timeline_block}
                        timelineBlockWords={transcript.timeline_block_words}
                        isMono={transcript.is_mono}
                        onWaveSurferReady={handleWaveSurfer}
                        autoScrollScope="event"
                    />
                );
            }
        }

        // Event Summary
        let eventSummaryDisplay: React.ReactElement;
        const automaticSummaryModule = data['addon-automatic-summary'] || data['automatic-summary'];
        if (eventDetails && automaticSummaryModule) {
            eventSummaryDisplay = (
                <EventSummary
                    eventDetails={eventDetails}
                    summaryToDisplay={automaticSummaryModule}
                    userData={userData}
                />
            );
        } else {
            eventSummaryDisplay = RenderNoDataAvailable();
        }

        // Event info card
        let eventInfoDisplay: React.ReactElement;
        if (eventDetails) {
            eventInfoDisplay = (
                <Col md={6}>
                    <Card className="mb-0 custom-border">
                        <div className="overflow-auto w-100">
                            <Table className="event-detail-table">
                                <thead>
                                    <tr>
                                        <th colSpan={2} className="align-content-center">{i18n.t('jt_call_information.text')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_event.text')}</h5>
                                            {eventDetails?.id ? (
                                                <span className="d-block event-detail-text-sm">{eventDetails.id}</span>
                                            ) : RenderNoDataAvailable()}
                                        </td>
                                        <td data-cy="external-id-container">
                                            <h5 className="d-block event-detail-external-id-header">{i18n.t('jt_external.text')}</h5>
                                            {eventDetails?.externalId ? (
                                                <div className="d-flex align-items-center event-detail-text-sm">
                                                    <span className="text-wrap">
                                                        {eventDetails.externalId}
                                                    </span>
                                                    <Button
                                                        variant="link"
                                                        className="p-0 text-decoration-none shadow-none text-secondary"
                                                        onClick={() => copyExternalId(eventDetails.externalId!)}
                                                        data-cy="copy-externalid-btn"
                                                        aria-label={i18n.t('es_copy_external.text')}
                                                    >
                                                        {extIdCopySuccess ? (
                                                            <Icon name="mdi-check" size="xs" className="text-info ml-1" />
                                                        ) : (
                                                            <Icon name="mdi-content-copy" size="xs" className="ml-1" />
                                                        )}
                                                    </Button>
                                                </div>
                                            ) : i18n.t('event_not_avb.text')}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_datetime.text')}</h5>
                                            {eventDetails?.datetime ? (
                                                <div className="d-block event-detail-text-sm">
                                                    <FormattedDateTime
                                                        dateTime={eventDetails.datetime}
                                                        showTime={false}
                                                        options={{zone: 'utc'}}
                                                    />
                                                </div>
                                            ) : RenderNoDataAvailable()}
                                        </td>

                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_language.text')}</h5>
                                            <div className="d-flex align-items-center event-detail-text-sm">
                                                <>
                                                    <span className="text-nowrap">
                                                        {Array.isArray(langFieldChoices) && langFieldChoices.find((x: LangFieldChoice) => x.value === eventDetails.lang)?.label || eventDetails.lang || 'Not selected'}
                                                    </span>
                                                    <Button
                                                        variant="link"
                                                        className="p-0 text-decoration-none shadow-none text-secondary"
                                                        onClick={() => setChangeLangModalOpen(true)}
                                                        data-cy="language-change-btn"
                                                        aria-label={i18n.t('es_change_language.text')}
                                                    >
                                                        <Icon name="mdi-pencil-outline" size="xs" className="ml-1" />
                                                    </Button>
                                                </>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_channel.text')}</h5>
                                            <span className="d-block event-detail-text-sm">{eventDetails?.channel?.name ? eventDetails.channel.name : RenderNoDataAvailable()}</span>
                                        </td>

                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_call_type.text')}</h5>
                                            <span className="d-block event-detail-text-sm">
                                                <RenderFieldValue value={eventDetails?.eventCallType} fieldName="eventCallType" restrictedFields={detailRestrictedFields} />
                                            </span>
                                        </td>
                                    </tr>
                                    <EventAgentCustomerNameBlock eventDetails={eventDetails} eventIndexDetails={eventIndexDetails} userData={userData} restrictedFields={detailRestrictedFields} />
                                </tbody>
                            </Table>
                        </div>
                    </Card>
                    <BaseModal
                        isOpen={changeLangModalOpen}
                        onClose={() => setChangeLangModalOpen(false)}
                        title={i18n.t('jt_change_language.text')}
                    >
                        <EventLangChangeForm
                            onDone={closeSettingsModal}
                            eventTypeId={eventDetails.eventType.id.toString()}
                        />
                    </BaseModal>
                </Col>
            );
        } else {
            eventInfoDisplay = RenderNoDataAvailable();
        }

        // Call Transcript
        let callTranscript: React.ReactElement | undefined;
        if (eventDetails?.eventTypeName === EVENT_TYPE_CALL) {
            if (loadingTranscript) {
                callTranscript = <CellLoader fullHeight={true} />;
            } else {
                if (transcript) {
                    callTranscript = (
                        <CallTranscript
                            eventId={activeEventId}
                            timelineBlock={transcript.timeline_block}
                            transcriptAvailable={!('error' in transcript)}
                            isMono={transcript.is_mono}
                            agentChannelId={Number(transcript.agent_channel_id)}
                            waveSurfer={waveSurfer}
                            isTranscriptionTabClicked={isTranscriptionTabClicked}
                            autoScrollScope="event"
                        />
                    );
                }
            }
        }

        const handleAddNote = (): void => {
            setModalMode('add');
            setSelectedComment(null);
            setCommentModalOpen(true);
        };

        const handleEditNote = (comment: EventComment): void => {
            setModalMode('edit');
            setSelectedComment(comment);
            setCommentModalOpen(true);
        };

        const NoteModal = ({show, onHide, eventId}: {show: boolean; onHide: () => void; eventId: number}): React.ReactElement => (
            <BaseModal
                isOpen={show}
                onClose={onHide}
                title={modalMode === 'add' ? i18n.t('es_add_note.text') : i18n.t('es_edit_note.text')}
            >
                <AddNoteForm
                    key={selectedComment ? selectedComment.id : 'new-comment'}
                    onDone={onHide}
                    eventId={eventId}
                    existingComment={selectedComment ? {
                        id: selectedComment.id,
                        comment: selectedComment.comment
                    } : undefined}
                    mode={modalMode}
                />
            </BaseModal>
        );

        // Event Notes
        let eventCommentsList: React.ReactElement | React.ReactElement[] | null;
        if (loadingEventComments) {
            eventCommentsList = <CellLoader />;
        } else if (Array.isArray(eventComments) && eventComments.length) {
            eventCommentsList = eventComments.map((x: EventComment, index: number) => (
                <div className="d-flex flex-row mb-3" key={index}>
                    <div className="w-100 mb-3">
                        <div className="d-flex flex-row align-self-stretch align-items-center justify-content-between">
                            <h5 className="m-0 event-detail-text-md">{x.user.userDisplayName || x.user.username}</h5>
                            {userData?.id === x.user.id && (
                                <Button
                                    variant="link"
                                    className="p-0 text-decoration-none shadow-none text-secondary"
                                    onClick={() => handleEditNote(x)}
                                    data-cy="note-edit-btn"
                                    aria-label={i18n.t('es_edit_note.text')}
                                >
                                    <Icon name="mdi-pencil-outline" size='xs' className="mt-0" />
                                </Button>
                            )}
                        </div>
                        <span className="text-muted event-detail-text-sm">
                            <FormattedDateTime
                                dateTime={x.createdOn}
                                showTime={false}
                                options={{setZone: false}}
                            />
                        </span>
                        <hr className="my-2 custom-separator" />
                        <div
                            className="comment-container event-detail-text-sm"
                            dangerouslySetInnerHTML={{__html: x.comment}}
                        />
                    </div>
                </div>
            ));
        } else {
            eventCommentsList = null;
        }

        // Notes
        const notesTab: React.ReactElement = (
            <>
                <Row className="mb-3">
                    <Col className="d-flex align-items-center">
                        <h4 className="mb-0 weight-300">{i18n.t('jt_notes.text')}</h4>
                    </Col>
                    <Col>
                        <Button
                            className="btn btn-info text-white float-right mb-0"
                            size='sm'
                            onClick={() => handleAddNote()}
                            data-cy="add-comment-button"
                        >
                            <span>
                                <Icon name="mdi-plus" className="mr-1" />
                                {i18n.t('jt_new_note.text')}
                            </span>
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="pr-2 event-detail-notes-container">
                            {eventCommentsList || <span className="text-muted">{i18n.t('jt_data_available.text')}</span>}
                        </div>
                    </Col>
                </Row>
                <NoteModal
                    show={commentModalOpen}
                    onHide={closeCommentModal}
                    eventId={typeof eventDetails?.id === 'number' ? eventDetails.id : Number(eventDetails?.id) || 0}
                    data-cy="note-modal"
                />
            </>
        );

        const openConfirmFunction = (
            confirmMessage: string,
            url: string,
            functionName: string,
            displayName?: string,
        ): void => {
            setConfirmAction({
                message: confirmMessage,
                url,
                functionName,
                displayName: displayName ?? functionName,
            });
            setConfirmModalOpen(true);
        };

        const closeConfirmModal = (): void => {
            setConfirmAction(null);
            setConfirmModalOpen(false);
            setIsExecuting(false);
        };

        const executeConfirmedAction = async (): Promise<void> => {
            if (!confirmAction) {return;}

            setIsExecuting(true);
            try {
                const response = await axios.post(confirmAction.url);
                if (confirmAction.functionName === 'Delete') {
                    const {getRootRedirectUrl} = await import('../../utils/rootUrlResolver');
                    window.location.href = getRootRedirectUrl({userData, menuStructure});
                } else if (confirmAction.functionName === DELETE_CALL_AUDIO_ACTION) {
                    showSuccess(i18n.t('jt_call_audio_deleted_successfully.text'));
                    closeConfirmModal();
                    window.location.reload();
                } else if (confirmAction.functionName === REPLACE_ORIGINAL_ACTION) {
                    showSuccess(i18n.t('jt_replace_original_successfully.text'));
                    closeConfirmModal();
                    window.location.reload();
                } else {
                    showSuccess(`${confirmAction.functionName} ${i18n.t('jt_started_successfully.text')}`);
                    closeConfirmModal();
                }
            } catch (error: unknown) {
                const errorMessage = extractErrorMessage(error, 'Unknown error');
                showError(`${confirmAction.displayName} ${i18n.t('jt_started_unsuccessfully.text')}, error: ${errorMessage}`);
                setIsExecuting(false);
            }
        };

        const insertToIndex = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_insert.text'),
                buildURL('eventInsertToIndex', {event_id: activeEventId}),
                i18n.t('insert_to_index.text'),
            );
        };

        const getAnonymisation = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_anonymize.text'),
                buildURL('eventGetAnonymization', {event_id: activeEventId}),
                i18n.t('anonymisation.text'),
            );
        };

        const getEvaluate = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_evaluate.text'),
                buildURL('eventEvaluate', {event_id: activeEventId}),
                i18n.t('evaluate.text'),
            );
        };

        const preProcess = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_pre_process.text'),
                buildURL('eventPreProcess', {event_id: activeEventId}),
                i18n.t('pre_process.text'),
            );
        };

        const postProcess = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_post_process.text'),
                buildURL('eventPostProcess', {event_id: activeEventId}),
                i18n.t('post_process.text'),
            );
        };

        const deliver = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_deliver.text'),
                buildURL('eventDeliver', {event_id: activeEventId}),
                i18n.t('deliver.text'),
            );
        };

        const ruleBasedActions = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_force_action.text'),
                buildURL('eventRuleBasedActions', {event_id: activeEventId}),
                i18n.t('rule_based_actions.text'),
            );
        };

        const deleteCallAudio = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_delete_audio.text'),
                buildURL('eventDeleteAudio', {event_id: activeEventId}),
                DELETE_CALL_AUDIO_ACTION,
                i18n.t('jt_delete_audio.text'),
            );
        };

        const replaceOriginal = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_replace_original.text'),
                buildURL('eventReplaceOriginal', {event_id: activeEventId}),
                REPLACE_ORIGINAL_ACTION,
                i18n.t('jt_replace_original.text'),
            );
        };

        const deleteEvent = async (): Promise<void> => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_delete.text'),
                buildURL('eventDelete', {event_id: activeEventId}),
                i18n.t('delete_event.text'),
            );
        };

        const eventActionModal = (): React.ReactElement => {
            const footer = (
                <Button
                    variant="info"
                    data-cy="copy-index-data-btn"
                    onClick={() => {
                        navigator.clipboard.writeText(actionModalContent).then(
                            () => {
                                showSuccess(i18n.t('content_copied_to_clipboard.text'));
                            },
                            (err: unknown) => {
                                console.error(i18n.t('failed_to_copy.text'), err);
                                showError(i18n.t('failed_to_copy_content.text'));
                            }
                        );
                    }}
                >
                    {i18n.t('jt_copy.text')}
                </Button>
            );

            return (
                <BaseModal
                    isOpen={actionModalOpen}
                    onClose={() => setActionModalOpen(false)}
                    title={actionModalTitle}
                    size="xl"
                    footer={footer}
                >
                    <div className="event-detail-action-modal-content">
                        <Card className="event-detail-action-modal-card">
                            <pre className="event-detail-action-modal-pre">
                                {actionModalContent}
                            </pre>
                        </Card>
                    </div>
                </BaseModal>
            );
        };

        const generateInsightText = (type: string, channelCompAvg: number, channelComp: number, percentageSameValue: number | undefined, percentageSameTopic: number | undefined): React.ReactElement | string => {
            if (type === 'numerical_descriptor') {
                const prefix = channelComp > 0 ? '+' : '';

                return (
                    <>
                        <label className="event-detail-text-lg"> {i18n.t('jt_channel_average.text')}
                            <Tooltip
                                content={i18n.t('jt_shows_how.text')}
                                placement="top"
                            >
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2 event-detail-tooltip-button"
                                    data-cy="event-detail-block-with-player-btn-7">
                                    <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                </button>
                            </Tooltip>
                        </label>
                        <label className="event-detail-text-xl">
                            <span>{prefix}{channelComp}%</span>
                            <span>{'  '}({channelCompAvg})</span>
                        </label>
                    </>
                );
            } else if (type === 'list_field' && percentageSameValue !== undefined) {
                return (
                    <>
                        <label className="event-detail-text-lg">{i18n.t('jt_channel_similarity.text')}
                            <Tooltip
                                content={i18n.t('jt_represents_the_category.text')}
                                placement="top"
                            >
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2 event-detail-tooltip-button"
                                    data-cy="event-detail-block-with-player-btn-6">
                                    <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                </button>
                            </Tooltip>
                        </label>
                        <label className="event-detail-text-xl">
                            <span>{percentageSameValue.toFixed(2)}%</span>
                        </label>
                    </>
                );
            } else if ((type === 'selection_field' || type === 'computed_classification') && percentageSameTopic !== undefined) {
                return (
                    <>
                        <label className="event-detail-text-lg">{i18n.t('jt_channel_similarity.text')}
                            <Tooltip
                                content={i18n.t('jt_represents_the_category.text')}
                                placement="top"
                            >
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2 event-detail-tooltip-button"
                                    data-cy="event-detail-block-with-player-btn-5">
                                    <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                </button>
                            </Tooltip>
                        </label>
                        <label className="event-detail-text-xl">
                            <span>{percentageSameTopic.toFixed(2)}%</span>
                        </label>
                    </>
                );
            }
            return i18n.t('jt_data_available.text');
        };

        const ModuleCards = ({data, evaluations, userData}: {data: {[key: string]: ModuleData}; evaluations: Evaluation[]; userData: UserData | null}) => {
            const [openStates, setOpenStates] = useState<{[key: string]: boolean}>(
                Object.keys(data).reduce((acc: {[key: string]: boolean}, key: string, index: number) => {
                    acc[key] = index === 0; // First card is open by default
                    return acc;
                }, {})
            );

            const getModuleNotAvailableText = (moduleKey: string, information?: ModuleData['information']) => {
                if (moduleKey === 'addon-quality-score') {
                    return i18n.t('jt_automatic_quality_score_not_included.text');
                }

                if (moduleKey === 'addon-call-metric-score') {
                    return i18n.t('jt_call_metric_score_not_included.text');
                }

                if (information?.text_if_module_not_available) {
                    return information.text_if_module_not_available;
                }

                return i18n.t('feature_not_included_in_package_and_contact_administrator.text', {
                    business_name: information?.business_name,
                });
            };

            return (
                <Col md={4}>
                    {Object.entries(data)
                        .filter(([moduleKey, moduleData]) => moduleData.overall) // Exclude modules without "overall"
                        .map(([moduleKey, moduleData]) => {
                            const isModuleAvailable = isModuleEnabled(userData, moduleKey);
                            const isOpen = openStates[moduleKey];
                            const toggleCard = () =>
                                setOpenStates((prev) => ({...prev, [moduleKey]: !prev[moduleKey]}));

                            // Special Case for 'insights' Module
                            if (moduleKey === 'insights') {
                                const topN = moduleData.overall?.top_n || 3;

                                // Process evaluations before sorting & filtering
                                const processedEvaluations = useMemo(() => {
                                    return evaluations.filter((x: Evaluation) => x.type !== 'list_field').flatMap((x: Evaluation) => {
                                        let channelComp = 0;
                                        let channelCompAvg = 0;

                                        if (x.channelComp?.avg) {
                                            channelComp = Number(((x.value / x.channelComp.avg) * 100 - 100).toFixed(2));
                                            channelCompAvg = Number(x.channelComp.avg.toFixed(2));
                                        }

                                        let eventTypeComp = 0;
                                        let eventTypeCompAvg = 0;
                                        if (x.eventTypeComp?.avg) {
                                            eventTypeComp = Number(((x.value / x.eventTypeComp.avg) * 100 - 100).toFixed(2));
                                            eventTypeCompAvg = Number(x.eventTypeComp.avg.toFixed(2));
                                        }

                                        const insight = generateInsightText(
                                            x.type,
                                            channelCompAvg,
                                            channelComp,
                                            x.percentage_same_value,
                                            x.percentage_same_topic
                                        );

                                        return [
                                            {
                                                id: x.id,
                                                label: x.label,
                                                value: x.value,
                                                channelAverage: channelCompAvg,
                                                channelComp: channelComp,
                                                eventTypeAverage: eventTypeCompAvg,
                                                eventTypeComp: eventTypeComp,
                                                description: x.description,
                                                insightText: insight,
                                                priority: x.priority || false,
                                            },
                                        ];
                                    });
                                }, [evaluations]);

                                // Sort and prioritize top evaluations
                                const topEvaluations = useMemo(() => {
                                    if (!processedEvaluations.length) {return [];}

                                    // Sort by highest absolute percentage change (`channelComp`)
                                    const sortedByChange = [...processedEvaluations].sort(
                                        (a, b) => Math.abs(b.channelComp) - Math.abs(a.channelComp)
                                    );

                                    // Take top 3 absolute changes, whether positive or negative
                                    const topChanges = sortedByChange.slice(0, 3);

                                    // Fill the rest with remaining evaluations to reach `topN` total
                                    const remainingEvaluations = sortedByChange.slice(3);

                                    return [...topChanges, ...remainingEvaluations].slice(0, topN);
                                }, [processedEvaluations, topN]);

                                return (
                                    <Card
                                        key={moduleKey}
                                        className="mb-3 event-detail-insights-card"
                                    >
                                        <Card.Header className="rounded bg-white event-detail-card-header-no-border">
                                            <div
                                                onClick={toggleCard}
                                                className="p-2 w-100 d-flex justify-content-between align-items-center text-dark event-detail-card-clickable"
                                            >
                                                <div className="d-flex align-items-center">
                                                    <h4 className="m-0">{moduleData.information?.business_name}</h4>
                                                    <Icon name={`mdi-chevron-${isOpen ? 'up' : 'down'}`} size="18px" className="ml-2" />
                                                </div>
                                                <h4 className="m-0 d-flex align-items-center">
                                                    Top {topEvaluations.length < topN && topEvaluations.length > 3 ? topEvaluations.length : topN}
                                                    <Tooltip
                                                        content={i18n.t('comparison_results_and_highlights.text', {threshold: appConfig.EVALUATION_TYPE_BEFORE_X_DAYS_DAYS_THRESHOLD})}
                                                        placement="top"
                                                    >
                                                        <span
                                                            className="ml-1 event-detail-card-clickable"
                                                        >
                                                            <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                                        </span>
                                                    </Tooltip>
                                                </h4>
                                            </div>
                                        </Card.Header>

                                        <Collapse in={isOpen}>
                                            <Card.Body className="pt-0">
                                                <hr className="mt-0" />
                                                {loadingEvaluations ? (
                                                    <p>{i18n.t('jt_loading_insights.text')}</p>
                                                ) : topEvaluations.length > 0 ? (
                                                    topEvaluations.map((evaluation) => {
                                                        return (
                                                            <div key={evaluation.id}>
                                                                <div className="d-flex justify-content-between align-items-center mt-2">
                                                                    {/* Label */}
                                                                    <label className="m-0 event-detail-label-gray">
                                                                        {evaluation.label}
                                                                        <Tooltip
                                                                            content={evaluation.description ? evaluation.description.replace(/<[^>]+>/g, '') :
                                                                                i18n.t('event_det_sum_no_desc.text')}
                                                                            placement="top"
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-link p-0 ml-2 event-detail-tooltip-button"
                                                                                data-cy="event-detail-block-with-player-btn-4">
                                                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                                                            </button>
                                                                        </Tooltip>
                                                                    </label>

                                                                    <h6 className="m-0 d-flex align-items-center">
                                                                        {/* Value/insights text Display */}
                                                                        <span>{evaluation.value}</span>
                                                                    </h6>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center mt-1">
                                                                    {evaluation.insightText}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <p>{i18n.t('jt_data_available.text')}</p>
                                                )}
                                            </Card.Body>
                                        </Collapse>
                                    </Card>
                                );
                            }

                            return (
                                <Card
                                    key={moduleKey}
                                    className="mb-3 event-detail-module-card"
                                    data-border-color={moduleData.overall.value ? getScoreColor(
                                        moduleData.overall.value * (moduleData.overall.multiplier || 1),
                                        moduleData
                                    ) : '#CCCCCC'}
                                >
                                    {/* Card Header */}
                                    <Card.Header className="rounded bg-white event-detail-card-header-no-border">
                                        <div
                                            onClick={toggleCard}
                                            className="p-2 w-100 d-flex justify-content-between align-items-center text-dark event-detail-card-clickable"
                                        >
                                            <div className="d-flex align-items-center">
                                                <h4 className="m-0">{moduleData.overall.label || i18n.t('overall_score.text')}</h4>
                                                <Icon name={`mdi-chevron-${isOpen ? 'up' : 'down'}`} size="18px" className="ml-2" />
                                            </div>
                                            <h4
                                                className="m-0 event-detail-module-score"
                                                data-score-color={moduleData.overall.value != null ? getScoreColor(moduleData.overall.value * (moduleData.overall.multiplier || 1), moduleData) : 'inherit'}
                                            >
                                                {moduleData.overall.value != null
                                                    ? `${moduleData.overall.value * (moduleData.overall.multiplier || 1)}%`
                                                    : ''}
                                            </h4>
                                        </div>
                                    </Card.Header>

                                    {/* Card Body */}
                                    <Collapse in={isOpen}>
                                        <Card.Body className="pt-0">
                                            <hr className="mt-0" />
                                            {isModuleAvailable ? (
                                                <>
                                                    {/* Render keys */}
                                                    {moduleData.keys?.map((keyItem) => (
                                                        <div
                                                            key={keyItem.key}
                                                            className="d-flex justify-content-between align-items-center mt-2"
                                                        >
                                                            <div className="d-inline-flex align-items-center">
                                                                <label className="m-0">{keyItem.label || '-'}</label>
                                                                <Tooltip
                                                                    content={keyItem.description ? keyItem.description.replace(/<[^>]+>/g, '') :
                                                                        'No description available'}
                                                                    placement="top"
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 ml-2 event-detail-tooltip-button"
                                                                        data-cy="event-detail-block-with-player-btn-3">
                                                                        <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                                                    </button>
                                                                </Tooltip>
                                                            </div>
                                                            <h6 className="m-0">
                                                                {keyItem.value != null
                                                                    ? `${keyItem.value}${keyItem.max_value ? `/${keyItem.max_value}` : ''}`
                                                                    : `-${keyItem.max_value ? `/${keyItem.max_value}` : ''}`}
                                                            </h6>
                                                        </div>
                                                    ))}

                                                    {/* Render additional information */}
                                                    {moduleData.additional?.length && moduleData.additional.length > 0 && <hr />}
                                                    {moduleData.additional?.map((additionalItem, index) => (
                                                        <div key={additionalItem.key} className="mt-2">
                                                            <h5 className="d-flex align-items-center">{additionalItem.label || '-'}
                                                                <Tooltip
                                                                    content={additionalItem.description ? additionalItem.description.replace(/<[^>]+>/g, '') :
                                                                        'No tooltip available'}
                                                                    placement="top"
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 ml-2 event-detail-tooltip-button"
                                                                        data-cy="event-detail-block-with-player-btn-2">
                                                                        <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                                                    </button>
                                                                </Tooltip>
                                                            </h5>
                                                            <label className="m-0">
                                                                {additionalItem.value || '-'}
                                                            </label>
                                                            {index < (moduleData.additional?.length || 0) - 1 && <hr />}
                                                        </div>
                                                    ))}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="overlay-text">
                                                        {getModuleNotAvailableText(moduleKey, moduleData.information)}
                                                    </span>

                                                    <div className="blur-container">
                                                        {/* Ensure at least 5 key items */}
                                                        {[
                                                            ...(moduleData.keys || []),
                                                            ...Array.from(
                                                                {length: Math.max(0, 5 - (moduleData.keys?.length || 0))},
                                                                (_, index) => ({
                                                                    key: `placeholder-${index}`,
                                                                    label: '-',
                                                                    max_value: '-',
                                                                    description: i18n.t('jt_data_available.text'),
                                                                    isPlaceholder: true, // Identify placeholders
                                                                })
                                                            ),
                                                        ].map((keyItem) => (
                                                            <div
                                                                key={keyItem.key}
                                                                className={`d-flex justify-content-between align-items-center mt-2 ${keyItem.isPlaceholder ? 'opacity-50' : '' // Style placeholders with lower opacity
                                                                    }`}
                                                            >
                                                                <div className="d-inline-flex align-items-center">
                                                                    <label className="m-0">{keyItem.label}</label>
                                                                    {!keyItem.isPlaceholder && (
                                                                        <Tooltip
                                                                            content={keyItem.description ? keyItem.description.replace(/<[^>]+>/g, '') :
                                                                                'No description available'}
                                                                            placement="top"
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-link p-0 ml-2 event-detail-tooltip-button"
                                                                                data-cy="event-detail-block-with-player-btn-1">
                                                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                                                            </button>
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                                <h6 className="m-0">{`-/ ${keyItem.max_value}`}</h6>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </Card.Body>
                                    </Collapse>
                                </Card>
                            );
                        })}
                </Col>
            );
        };

        const getScoreColor = (score: number | null, config: ModuleData): string => {
            if (score == null) {
                return '#CCCCCC';
            }

            const scoreString = score.toString();
            const scoreValue = parseInt(scoreString.replace('%', ''), 10);

            // Get thresholds from config (default to 80 and 40 if not specified)
            const highThreshold = config?.overall?.high_threshold ?? 80;
            const lowThreshold = config?.overall?.low_threshold ?? 40;

            if (scoreValue >= highThreshold) {return '#55ce63';}
            if (scoreValue <= lowThreshold) {return 'red';}
            return '#EFAD4F';
        };
        return (
            <>
                {/*Event Header*/}
                <Row>
                    <Col md={12}>
                        <Card className="mb-3">
                            <Card.Body>
                                {eventDetails ? (
                                    <Row>
                                        <Col>
                                            <div className="d-flex justify-content-between align-items-center">

                                                <EventHeader
                                                    eventType={eventDetails.eventTypeName}
                                                    eventId={String(eventDetails.id)}
                                                    channelName={eventDetails.channel?.name}
                                                    dateTime={eventDetails.datetime}
                                                    eventAgent={Array.isArray(eventAgent) ? eventAgent[0]?.agentName || '' : (typeof eventAgent === 'object' && eventAgent?.agentName ? eventAgent.agentName : String(eventAgent || ''))}
                                                    eventCustomer={eventCustomer?.customerName || ''}
                                                    restrictedFields={detailRestrictedFields}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                ) : RenderNoDataAvailable()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/*Audio Player*/}
                <Row>
                    <Col md={12}>
                        <Card className="mb-3">
                            <Card.Body>
                                {audioPlayer}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/*Tabs*/}
                <Row>
                    <Col md={8}>
                        <div className="card mb-3">
                            <div className="card-body">
                                <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
                                    <div className="d-flex align-items-center">
                                        <Nav variant="tabs" className="d-flex w-100 mb-3 justify-content-between">
                                            <div className="d-flex flex-wrap">
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs" eventKey="call-details">{i18n.t('jt_call_details.text')}</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs"
                                                        eventKey="transcript">{i18n.t('jt_transcription.text')}</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs"
                                                        eventKey="evaluations">{i18n.t('jt_evaluations.text')}</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs"
                                                        eventKey="notes">{i18n.t('jt_notes.text')}</Nav.Link>
                                                </Nav.Item>
                                                {
                                                    canAccessAudit ? (
                                                        <Nav.Item>
                                                            <Nav.Link className="custom-tabs"
                                                                eventKey="audit">{i18n.t('jt_audit.text')}</Nav.Link>
                                                        </Nav.Item>
                                                    ) : null
                                                }
                                            </div>

                                            {(() => {
                                                const hasViewItems = (eventDetails?.eventType?.name === EVENT_TYPE_CALL && hasPermission('fs.event.call.transcription.read', userData?.permissions, true))
                                                    || hasPermission('fs.event.index_data.read', userData?.permissions, true);
                                                const hasActionItems = hasPermission('fs.event.insert_to_index.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.anonymisation.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.evaluation.execute', userData?.permissions, true)
                                                    || (eventDetails?.eventType?.name === EVENT_TYPE_CALL && hasPermission('fs.event.pre_process.execute', userData?.permissions, true))
                                                    || hasPermission('fs.event.post_process.execute', userData?.permissions, true)
                                                    || (eventDetails?.eventType?.name === EVENT_TYPE_CALL && hasPermission('fs.event.delivery.execute', userData?.permissions, true))
                                                    || hasPermission('fs.event.rule_based_action.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.delete', userData?.permissions, true);
                                                const hasAnyItems = hasViewItems || hasActionItems;

                                                return (
                                                    <Nav.Item>
                                                        <Tooltip content={hasAnyItems ? i18n.t('jt_actions.text') : i18n.t('tt_no_actions_available.text')} placement="top">
                                                            {hasAnyItems ? (
                                                                <Dropdown>
                                                                    <Dropdown.Toggle
                                                                        as={Button}
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        icon="mdi-dots-horizontal"
                                                                        className="dropdown-toggle-no-caret"
                                                                        data-cy="event-actions-toggle"
                                                                        id="settings-dropdown-1"
                                                                    />

                                                                    <Dropdown.Menu align="end">
                                                                        {hasViewItems && (
                                                                            <Dropdown.Header
                                                                                className="custom-dropdown-header text-muted"> {i18n.t('jt_view.text')}</Dropdown.Header>
                                                                        )}
                                                                        {eventDetails?.eventType?.name === EVENT_TYPE_CALL && hasPermission('fs.event.call.transcription.read', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={() => {
                                                                                        setActionModalOpen(true);
                                                                                        setActionModalTitle(i18n.t('jt_transcriptions.text'));
                                                                                        setActionModalContent(JSON.stringify(eventDetails?.eventCallTrans, null, 2));
                                                                                    }}
                                                                                    className="d-inline-block"
                                                                                >
                                                                                    {i18n.t('jt_transcriptions.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.index_data.read', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={() => {
                                                                                        setActionModalOpen(true);
                                                                                        setActionModalTitle(i18n.t('jt_registry_data.text'));
                                                                                        setActionModalContent(JSON.stringify(eventIndexDetails, null, 2));
                                                                                    }}
                                                                                    className="d-inline-block"
                                                                                >
                                                                                    {i18n.t('jt_registry_data.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasActionItems && (
                                                                            <Dropdown.Header
                                                                                className="custom-dropdown-header text-muted">{i18n.t('jt_action.text')}</Dropdown.Header>
                                                                        )}
                                                                        {hasPermission('fs.event.insert_to_index.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={insertToIndex}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-with-player-jtinsertindex-text-btn">
                                                                                    {i18n.t('jt_insert_index.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.anonymisation.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={getAnonymisation}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-with-player-jtanonymise-text-btn">
                                                                                    {i18n.t('jt_anonymise.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.evaluation.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={getEvaluate}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-with-player-jtevaluate-text-btn">
                                                                                    {i18n.t('jt_evaluate.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {eventDetails?.eventType?.name === EVENT_TYPE_CALL && hasPermission('fs.event.pre_process.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={preProcess}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-with-player-jtpreprocess-text-btn">
                                                                                    {i18n.t('jt_pre_process.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.post_process.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={postProcess}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-with-player-jtpostprocess-text-btn">
                                                                                    {i18n.t('jt_post_process.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {eventDetails?.eventType?.name === EVENT_TYPE_CALL && hasPermission('fs.event.delivery.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={deliver}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-with-player-jtdeliver-text-btn">
                                                                                    {i18n.t('jt_deliver.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.rule_based_action.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={ruleBasedActions}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-with-player-jtforceaction-text-btn">
                                                                                    {i18n.t('jt_force_action.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {eventDetails?.eventType?.name === EVENT_TYPE_CALL && transcript?.has_audio && hasPermission('fs.event.delete', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={deleteCallAudio}
                                                                                    className="d-inline-block text-danger"
                                                                                    data-cy="event-detail-block-with-player-jtdeleteaudio-text-btn">
                                                                                    {i18n.t('jt_delete_audio.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.delete', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={deleteEvent}
                                                                                    className="d-inline-block text-danger"
                                                                                    data-cy="event-detail-block-with-player-jtdelete20-text-btn">
                                                                                    {i18n.t('jt_delete.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {eventActionModal()}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            ) : (
                                                                <span className="d-inline-block">
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        icon="mdi-dots-horizontal"
                                                                        className="dropdown-toggle-no-caret"
                                                                        data-cy="event-actions-toggle"
                                                                        disabled
                                                                        style={{pointerEvents: 'none'}}
                                                                    />
                                                                </span>
                                                            )}
                                                        </Tooltip>
                                                    </Nav.Item>
                                                );
                                            })()}
                                        </Nav>
                                    </div>


                                    <Tab.Content>
                                        <Tab.Pane eventKey="call-details">
                                            <Row id="event-detail-display">
                                                {eventSummaryDisplay}
                                                {eventInfoDisplay}
                                            </Row>
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="transcript">
                                            {callTranscript}
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="evaluations">
                                            <EventEvaluationsTab eventId={String(activeEventId)}
                                                isEvaluationsTabClicked={isEvaluationsTabClicked} />
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="notes">
                                            {notesTab}
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="audit">
                                            {eventDetails ? (
                                                <AuditTab eventDetails={eventDetails as any} userData={userData}
                                                    isAuditTabClicked={isAuditTabClicked} />
                                            ) : (
                                                <RenderNoDataAvailable />
                                            )}
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Tab.Container>
                            </div>
                        </div>
                    </Col>

                    {userData && data && <ModuleCards data={data} evaluations={evaluations} userData={userData} />}
                </Row>
                <ModalAlert
                    isOpen={confirmModalOpen}
                    onClose={closeConfirmModal}
                    variant="warning"
                    title={i18n.t('jt_confirm_action.text')}
                    message={confirmAction?.message || ''}
                    actions={[
                        {
                            label: i18n.t('jt_confirm.text'),
                            onClick: executeConfirmedAction,
                            variant: 'primary',
                            loading: isExecuting
                        }
                    ]}
                    dismissible={!isExecuting}
                />
            </>
        );
    }
);

export default EventDetailBlockWithPlayer;
